import React, { useState, useRef, useEffect, useCallback } from 'react'
import { streamChatMessage, getChatSessions, getSessionMessages, getDraft, updateSessionModel, ChatMessage, ChatSession, Draft } from '../lib/api'
import DraftEditor from '../components/DraftEditor'

const MODEL_OPTIONS = [
  { value: 'claude-sonnet-4-6', label: 'Sonnet', short: 'S' },
  { value: 'claude-opus-4-7', label: 'Opus', short: 'O' },
]

export default function ChatPage() {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [sessionId, setSessionId] = useState<number | undefined>(() => {
    const saved = localStorage.getItem('hq-chat-session')
    return saved ? parseInt(saved, 10) || undefined : undefined
  })
  const [sessions, setSessions] = useState<ChatSession[]>([])
  const [streaming, setStreaming] = useState(false)
  const [streamingText, setStreamingText] = useState('')
  const [showSessions, setShowSessions] = useState(false)
  const [pastedImages, setPastedImages] = useState<string[]>([])  // Base64 image data URIs
  const [selectedModel, setSelectedModel] = useState<string>(() =>
    localStorage.getItem('hq-model') || 'claude-sonnet-4-6'
  )
  const bottomRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const messageQueue = useRef<Array<{ text: string; images?: string[] }>>([])
  const chatBodyRef = useRef<HTMLDivElement>(null)

  // Draggable divider between messages and input
  const [readFlex, setReadFlex] = useState(3)
  const [writeFlex, setWriteFlex] = useState(1)

  const onDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const container = chatBodyRef.current
    if (!container) return
    const startY = e.clientY
    const totalFlex = readFlex + writeFlex
    const containerHeight = container.getBoundingClientRect().height
    const startReadFlex = readFlex
    const minReadPx = 80
    const minWritePx = 80

    function onMove(ev: MouseEvent) {
      const delta = ev.clientY - startY
      const deltaFlex = (delta / containerHeight) * totalFlex
      const newRead = Math.max(
        (minReadPx / containerHeight) * totalFlex,
        Math.min((containerHeight - minWritePx) / containerHeight * totalFlex, startReadFlex + deltaFlex)
      )
      setReadFlex(newRead)
      setWriteFlex(totalFlex - newRead)
    }

    function onUp() {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [readFlex, writeFlex])

  // Persist sessionId to localStorage
  useEffect(() => {
    if (sessionId) {
      localStorage.setItem('hq-chat-session', String(sessionId))
    }
  }, [sessionId])

  // On mount: load sessions and restore active session messages
  useEffect(() => {
    loadSessions()
    if (sessionId) {
      getSessionMessages(sessionId)
        .then(data => {
          if (data.messages?.length) setMessages(data.messages)
        })
        .catch(() => {
          // Session gone — clear stale reference
          localStorage.removeItem('hq-chat-session')
          setSessionId(undefined)
        })
    }
  }, [])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingText])

  async function loadSessions() {
    try {
      const data = await getChatSessions()
      setSessions(data.sessions)
    } catch {}
  }

  async function loadSession(id: number) {
    try {
      const data = await getSessionMessages(id)
      setSessionId(id)
      setMessages(data.messages)
      setShowSessions(false)
      // Load session's model preference
      const session = sessions.find(s => s.id === id)
      if (session?.model) {
        setSelectedModel(session.model)
      }
    } catch {}
  }

  async function handleModelChange(model: string) {
    setSelectedModel(model)
    localStorage.setItem('hq-model', model)
    if (sessionId) {
      try { await updateSessionModel(sessionId, model) } catch {}
    }
  }

  async function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const items = e.clipboardData?.items
    if (!items) return

    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (item.type.startsWith('image/')) {
        e.preventDefault()
        const file = item.getAsFile()
        if (file) {
          const reader = new FileReader()
          reader.onload = (event) => {
            const dataUri = event.target?.result as string
            setPastedImages(prev => [...prev.slice(-2), dataUri])
          }
          reader.readAsDataURL(file)
        }
      }
    }
  }

  async function processMessage(text: string, images?: string[]) {
    setStreaming(true)
    setStreamingText('')

    const userMsg: ChatMessage = {
      id: Date.now(),
      role: 'user',
      content: text,
      entities_referenced: null,
      created_at: new Date().toISOString(),
      images,
    }
    setMessages(prev => [...prev, userMsg])

    let fullResponse = ''

    try {
      for await (const chunk of streamChatMessage(text, sessionId, null, images, sessionId ? null : selectedModel)) {
        if (chunk.type === 'text') {
          fullResponse += chunk.content ?? ''
          setStreamingText(fullResponse)
        } else if (chunk.type === 'done') {
          setSessionId(chunk.session_id)
          let draft: Draft | undefined
          if (chunk.draft_id) {
            try { draft = await getDraft(chunk.draft_id) } catch {}
          }
          const assistantMsg: ChatMessage = {
            id: Date.now() + 1,
            role: 'assistant',
            content: fullResponse,
            entities_referenced: chunk.entities_referenced ?? null,
            created_at: new Date().toISOString(),
            draft,
          }
          setMessages(prev => [...prev, assistantMsg])
          setStreamingText('')
          loadSessions()
        }
      }
    } catch (e) {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: `Error: ${e instanceof Error ? e.message : 'Failed to get response'}`,
        entities_referenced: null,
        created_at: new Date().toISOString(),
      }])
      setStreamingText('')
    } finally {
      const next = messageQueue.current.shift()
      if (next) {
        await processMessage(next.text, next.images)
      } else {
        setStreaming(false)
      }
    }
  }

  function sendMessage() {
    const text = input.trim()
    if (!text && pastedImages.length === 0) return
    const images = pastedImages.length > 0 ? [...pastedImages] : undefined
    setInput('')
    setPastedImages([])

    if (streaming) {
      messageQueue.current.push({ text, images })
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'user',
        content: text,
        entities_referenced: null,
        created_at: new Date().toISOString(),
        images,
      }])
      return
    }

    processMessage(text, images)
  }

  function newSession() {
    setSessionId(undefined)
    setMessages([])
    setStreamingText('')
    setShowSessions(false)
    localStorage.removeItem('hq-chat-session')
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  function renderContent(text: string) {
    // Render phone numbers as tappable links
    return text.replace(
      /(\+?1?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})/g,
      '<a href="tel:$1" style="color:#4f9cf9">$1</a>'
    )
  }

  return (
    <div className="chat-page">
      <div className="chat-toolbar">
        <button className="chat-btn secondary" onClick={() => setShowSessions(!showSessions)}>
          Sessions
        </button>
        <button className="chat-btn secondary" onClick={newSession}>
          New
        </button>
        {sessionId && (
          <span className="chat-session-label">Session #{sessionId}</span>
        )}
        <div className="model-picker" style={{ marginLeft: 'auto', display: 'flex', gap: '2px' }}>
          {MODEL_OPTIONS.map(m => (
            <button
              key={m.value}
              className={`chat-btn ${selectedModel === m.value ? 'primary' : 'secondary'}`}
              onClick={() => handleModelChange(m.value)}
              style={{ padding: '4px 10px', fontSize: '12px', minWidth: 'auto' }}
              title={m.value}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {showSessions && (
        <div className="chat-sessions-panel">
          {sessions.length === 0 && <div className="chat-empty">No sessions yet</div>}
          {sessions.map(s => (
            <div
              key={s.id}
              className={`chat-session-item ${s.id === sessionId ? 'active' : ''}`}
              onClick={() => loadSession(s.id)}
            >
              <div className="session-title">{s.title || `Session #${s.id}`}</div>
              <div className="session-date">
                {s.last_message_at ? new Date(s.last_message_at).toLocaleDateString() : ''}
              </div>
            </div>
          ))}
        </div>
      )}

      <div ref={chatBodyRef} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      <div className="chat-messages" style={{ flex: readFlex, overflow: 'auto' }}>
        {messages.length === 0 && !streaming && (
          <div className="chat-empty-state">
            <p>Ask HQ anything about your clients, contacts, or open items.</p>
            <p className="chat-hint">Try: "What's open with MyTown?" or "Who is Rocky?"</p>
          </div>
        )}

        {messages.map(msg => (
          <div key={msg.id} className={`chat-message ${msg.role}`}>
            <div className="message-role">{msg.role === 'user' ? 'You' : 'HQ'}</div>
            {msg.images && msg.images.length > 0 && (
              <div style={{ marginBottom: '8px' }}>
                {msg.images.map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    alt={`Image ${i + 1}`}
                    style={{ maxWidth: '100%', maxHeight: '300px', marginRight: '8px', marginBottom: '8px', borderRadius: '4px' }}
                  />
                ))}
              </div>
            )}
            <div
              className="message-content"
              dangerouslySetInnerHTML={{ __html: renderContent(msg.content) }}
            />
            {msg.draft && (
              <DraftEditor draft={msg.draft} />
            )}
          </div>
        ))}

        {streaming && streamingText && (
          <div className="chat-message assistant streaming">
            <div className="message-role">HQ</div>
            <div
              className="message-content"
              dangerouslySetInnerHTML={{ __html: renderContent(streamingText) }}
            />
            <span className="streaming-cursor">▋</span>
          </div>
        )}

        {streaming && !streamingText && (
          <div className="chat-message assistant streaming">
            <div className="message-role">HQ</div>
            <div className="message-content thinking">Thinking...</div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Draggable divider */}
      <div
        onMouseDown={onDividerMouseDown}
        style={{
          height: 6, cursor: 'row-resize', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(255,255,255,0.05)', flexShrink: 0, userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 10, opacity: 0.4, letterSpacing: 2 }}>⬍</span>
      </div>

      <div className="chat-input-area" style={{ flex: writeFlex, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {pastedImages.length > 0 && (
          <div style={{ marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {pastedImages.map((img, i) => (
              <div key={i} style={{ position: 'relative', display: 'inline-block' }}>
                <img src={img} alt={`Pasted ${i + 1}`} style={{ maxHeight: '60px', borderRadius: '4px' }} />
                <button
                  style={{
                    position: 'absolute', top: '-5px', right: '-5px',
                    width: '20px', height: '20px', padding: 0,
                    borderRadius: '50%', background: '#999', color: '#fff',
                    border: 'none', cursor: 'pointer', fontSize: '12px'
                  }}
                  onClick={() => setPastedImages(prev => prev.filter((_, j) => j !== i))}
                  title="Remove image"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
        <textarea
          ref={textareaRef}
          className="chat-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={streaming ? 'Type to queue next message...' : 'Ask HQ anything... (paste images here)'}
          style={{ flex: 1, resize: 'none' }}
        />
        <button
          className="chat-btn primary"
          onClick={sendMessage}
          disabled={input.trim().length === 0 && pastedImages.length === 0}
        >
          {streaming ? '...' : 'Send'}
        </button>
      </div>
      </div>{/* end flex container */}
    </div>
  )
}
